import $ from 'jquery';
import React, {lazy, Suspense} from 'react';
import {createRoot} from 'react-dom/client';
import {Sentry} from '../Core/sentry';
import {browser} from '../Core/Browser';
import {glossar} from '../Core/Glossar';
import {i18n} from './Core/i18n';
import {LoadingScreen} from '../AppInterface/elements/Loaders/LoadingScreen';
import {importFontello} from '../Common/Fonts/fonts';
import {ChartPortals} from '../AppInterface/elements/ChartPortals/ChartPortals';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';
import '../Common/Styles/_froala.scss';

window.Browser = browser;
window.Glossar = glossar;
window.translate = i18n.t;
importFontello();

// Provided by survey/dependencies.js
type TLegacyRespondent = {initRespondend: () => void};

// Survey.tpl: server-rendered poll driven by the legacy respondent, React only adds ChartPortals
const mountChartPortals = (root: HTMLElement): void => {
  Sentry.setTag('interface', 'survey');
  Sentry.setTag('account', document.getElementById('poll')?.getAttribute('accId'));
  window.Sentry = Sentry;

  $(() => (window as unknown as {LP: TLegacyRespondent}).LP.initRespondend());

  createRoot(root).render(<ChartPortals />);

  // iframe-resizer 4.x observes DOM mutations, but a font swap can change height without one.
  void document.fonts?.ready.then(() => {
    // If fonts finish before the resizer is ready, its initial measurement includes them.
    (window as Window & {parentIFrame?: {size: () => void}}).parentIFrame?.size();
  });
};

// SurveyInterface.tpl: SPA for blacklist and shared reports
const mountSurveyApp = (root: HTMLElement): void => {
  Sentry.setTag('interface', 'survey-app');

  // Lazy so the iframe breakout and the global styles in SurveyApp never reach embedded surveys
  const SurveyApp = lazy(() => import('./SurveyApp'));

  createRoot(root).render(
    <Suspense fallback={<LoadingScreen />}>
      <SurveyApp />
    </Suspense>
  );
};

export const mountSurveyInterface = (): void => {
  const surveyRoot = document.getElementById('survey-react-root'),
    appRoot = document.getElementById('app-root');

  if (surveyRoot) {
    mountChartPortals(surveyRoot);
  } else if (appRoot) {
    mountSurveyApp(appRoot);
  }
};

mountSurveyInterface();
